import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner, faCheckCircle, faExclamationTriangle,
    faImage, faPlus, faTrashAlt, faUsers, faUser,
    faChurch, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import './JoinPage.css';

// Determine functions base URL
const getFunctionsBaseUrl = () => {
    const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';
    if (useEmulators) {
        return 'http://127.0.0.1:5001/churchaware-8f158/us-central1';
    }
    return 'https://us-central1-churchaware-8f158.cloudfunctions.net';
};

const FUNCTIONS_BASE_URL = getFunctionsBaseUrl();

// Helper functions for dropdowns
const getMonths = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) });
    }
    return months;
};

const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && year >= 1800) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};

const getYears = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 100; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
    'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const emptyMember = () => ({
    name: '',
    email: '',
    cellphone: '',
    birthdayMonth: '',
    birthdayDay: '',
    birthdayYear: '',
});

function JoinPage() {
    const { tokenId } = useParams();

    // Token validation state
    const [tokenStatus, setTokenStatus] = useState('loading'); // loading, valid, expired, invalid, error
    const [orgName, setOrgName] = useState('');

    // Family form state
    const [surname, setSurname] = useState('');
    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [anniversaryMonth, setAnniversaryMonth] = useState('');
    const [anniversaryDay, setAnniversaryDay] = useState('');
    const [anniversaryYear, setAnniversaryYear] = useState('');

    // Photo state
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    // Members state
    const [members, setMembers] = useState([emptyMember()]);

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await fetch(
                    `${FUNCTIONS_BASE_URL}/getJoinTokenInfo?tokenId=${encodeURIComponent(tokenId)}`
                );
                const data = await response.json();

                if (response.ok && data.valid) {
                    setOrgName(data.orgName);
                    setTokenStatus('valid');
                } else if (response.status === 410) {
                    setTokenStatus('expired');
                } else if (response.status === 404) {
                    setTokenStatus('invalid');
                } else {
                    setTokenStatus('error');
                }
            } catch (err) {
                console.error('Error validating token:', err);
                setTokenStatus('error');
            }
        };

        if (tokenId) {
            validateToken();
        } else {
            setTokenStatus('invalid');
        }
    }, [tokenId]);

    // Handle photo selection
    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            setErrorMessage('Photo must be smaller than 10MB.');
            return;
        }

        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreviewUrl(reader.result);
        reader.readAsDataURL(file);
    };

    const removePhoto = () => {
        setImageFile(null);
        setImagePreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Member management
    const addMember = () => {
        setMembers([...members, emptyMember()]);
    };

    const removeMember = (index) => {
        if (members.length <= 1) return;
        setMembers(members.filter((_, i) => i !== index));
    };

    const updateMember = (index, field, value) => {
        const updated = [...members];
        updated[index] = { ...updated[index], [field]: value };
        setMembers(updated);
    };

    // Form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Basic validation
        if (!surname.trim()) {
            setErrorMessage('Family surname is required.');
            return;
        }
        if (!members[0].name.trim()) {
            setErrorMessage('At least one family member name is required.');
            return;
        }
        for (let i = 0; i < members.length; i++) {
            if (!members[i].name.trim()) {
                setErrorMessage(`Member ${i + 1} must have a name.`);
                return;
            }
        }

        setIsSubmitting(true);

        try {
            // Convert photo to base64 if present
            let familyPhoto = null;
            let familyPhotoType = null;
            if (imageFile) {
                const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        // Strip the data:image/xxx;base64, prefix
                        const result = reader.result;
                        const base64Data = result.split(',')[1];
                        resolve(base64Data);
                    };
                    reader.readAsDataURL(imageFile);
                });
                familyPhoto = base64;
                familyPhotoType = imageFile.type;
            }

            const requestBody = {
                tokenId,
                family: {
                    surname: surname.trim(),
                    phone: phone.trim() || undefined,
                    street: street.trim() || undefined,
                    city: city.trim() || undefined,
                    state: state || undefined,
                    zipcode: zipcode.trim() || undefined,
                    anniversaryMonth: anniversaryMonth || undefined,
                    anniversaryDay: anniversaryDay || undefined,
                    anniversaryYear: anniversaryYear || undefined,
                },
                members: members.map(m => ({
                    name: m.name.trim(),
                    email: m.email.trim() || undefined,
                    cellphone: m.cellphone.trim() || undefined,
                    birthdayMonth: m.birthdayMonth || undefined,
                    birthdayDay: m.birthdayDay || undefined,
                    birthdayYear: m.birthdayYear || undefined,
                })),
                familyPhoto: familyPhoto || undefined,
                familyPhotoType: familyPhotoType || undefined,
            };

            const response = await fetch(`${FUNCTIONS_BASE_URL}/submitJoinRequest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus('success');
            } else {
                setErrorMessage(data.error || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            console.error('Error submitting form:', err);
            setErrorMessage('Failed to submit. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render loading state
    if (tokenStatus === 'loading') {
        return (
            <div className="join-page">
                <div className="join-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    // Render expired state
    if (tokenStatus === 'expired') {
        return (
            <div className="join-page">
                <div className="join-expired">
                    <div className="join-expired-icon">
                        <FontAwesomeIcon icon={faTimesCircle} />
                    </div>
                    <h2>Link Expired</h2>
                    <p>This join link has expired. Please ask a church administrator for a new QR code.</p>
                </div>
            </div>
        );
    }

    // Render invalid state
    if (tokenStatus === 'invalid' || tokenStatus === 'error') {
        return (
            <div className="join-page">
                <div className="join-expired">
                    <div className="join-expired-icon">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <h2>Invalid Link</h2>
                    <p>This link is not valid. Please ask a church administrator for a new QR code.</p>
                </div>
            </div>
        );
    }

    // Render success state
    if (submitStatus === 'success') {
        return (
            <div className="join-page">
                <div className="join-header">
                    <h1><FontAwesomeIcon icon={faChurch} /> {orgName}</h1>
                </div>
                <div className="join-success">
                    <div className="join-success-icon">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h2>Thank You!</h2>
                    <p>Your information has been submitted for review. A church administrator will review your submission and add you to the directory.</p>
                </div>
            </div>
        );
    }

    // Render form
    return (
        <div className="join-page">
            <div className="join-header">
                <h1><FontAwesomeIcon icon={faChurch} /> {orgName}</h1>
                <p>Join our church directory</p>
            </div>

            <div className="join-form-container">
                <form onSubmit={handleSubmit}>
                    {errorMessage && (
                        <div className="join-error-msg">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            {errorMessage}
                        </div>
                    )}

                    {/* Family Information */}
                    <div className="join-form-card">
                        <h2><FontAwesomeIcon icon={faUsers} /> Family Information</h2>

                        <div className="join-form-group">
                            <label>Family Surname *</label>
                            <input
                                type="text"
                                value={surname}
                                onChange={(e) => setSurname(e.target.value)}
                                placeholder="Last name"
                                required
                                className={!surname.trim() && errorMessage ? 'error' : ''}
                            />
                        </div>

                        <div className="join-form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="(555) 123-4567"
                            />
                        </div>

                        <div className="join-form-group">
                            <label>Street Address</label>
                            <input
                                type="text"
                                value={street}
                                onChange={(e) => setStreet(e.target.value)}
                                placeholder="123 Main St"
                            />
                        </div>

                        <div className="join-form-row">
                            <div className="join-form-group">
                                <label>City</label>
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="City"
                                />
                            </div>
                            <div className="join-form-group">
                                <label>State</label>
                                <select value={state} onChange={(e) => setState(e.target.value)}>
                                    <option value="">Select</option>
                                    {US_STATES.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="join-form-group">
                                <label>Zip</label>
                                <input
                                    type="text"
                                    value={zipcode}
                                    onChange={(e) => setZipcode(e.target.value)}
                                    placeholder="12345"
                                    maxLength={10}
                                />
                            </div>
                        </div>

                        <div className="join-form-group">
                            <label>Wedding Anniversary</label>
                            <div className="join-form-row">
                                <div className="join-form-group">
                                    <select value={anniversaryMonth} onChange={(e) => setAnniversaryMonth(e.target.value)}>
                                        <option value="">Month</option>
                                        {getMonths().map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="join-form-group">
                                    <select value={anniversaryDay} onChange={(e) => setAnniversaryDay(e.target.value)}>
                                        <option value="">Day</option>
                                        {getDaysInMonth(anniversaryMonth, anniversaryYear).map(d => (
                                            <option key={d.value} value={d.value}>{d.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="join-form-group">
                                    <select value={anniversaryYear} onChange={(e) => setAnniversaryYear(e.target.value)}>
                                        <option value="">Year</option>
                                        {getYears().map(y => (
                                            <option key={y.value} value={y.value}>{y.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Photo Upload */}
                        <div className="join-form-group">
                            <label>Family Photo</label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/jpeg,image/png"
                                onChange={handlePhotoSelect}
                                style={{ display: 'none' }}
                            />
                            {imagePreviewUrl ? (
                                <div className="join-photo-preview">
                                    <img src={imagePreviewUrl} alt="Family preview" />
                                    <button
                                        type="button"
                                        className="join-photo-remove"
                                        onClick={removePhoto}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    className="join-photo-upload"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <FontAwesomeIcon icon={faImage} />
                                    <p>Tap to add a family photo (optional)</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Family Members */}
                    <div className="join-form-card">
                        <h2><FontAwesomeIcon icon={faUser} /> Family Members</h2>

                        {members.map((member, index) => (
                            <div key={index} className="join-member-card">
                                <div className="join-member-card-header">
                                    <h3>Member {index + 1}</h3>
                                    {members.length > 1 && (
                                        <button
                                            type="button"
                                            className="join-member-remove"
                                            onClick={() => removeMember(index)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} /> Remove
                                        </button>
                                    )}
                                </div>

                                <div className="join-form-group">
                                    <label>First Name *</label>
                                    <input
                                        type="text"
                                        value={member.name}
                                        onChange={(e) => updateMember(index, 'name', e.target.value)}
                                        placeholder="First name"
                                        required
                                    />
                                </div>

                                <div className="join-form-row">
                                    <div className="join-form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={member.email}
                                            onChange={(e) => updateMember(index, 'email', e.target.value)}
                                            placeholder="email@example.com"
                                        />
                                    </div>
                                    <div className="join-form-group">
                                        <label>Cell Phone</label>
                                        <input
                                            type="tel"
                                            value={member.cellphone}
                                            onChange={(e) => updateMember(index, 'cellphone', e.target.value)}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                </div>

                                <div className="join-form-group">
                                    <label>Birthday</label>
                                    <div className="join-form-row">
                                        <div className="join-form-group">
                                            <select
                                                value={member.birthdayMonth}
                                                onChange={(e) => updateMember(index, 'birthdayMonth', e.target.value)}
                                            >
                                                <option value="">Month</option>
                                                {getMonths().map(m => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="join-form-group">
                                            <select
                                                value={member.birthdayDay}
                                                onChange={(e) => updateMember(index, 'birthdayDay', e.target.value)}
                                            >
                                                <option value="">Day</option>
                                                {getDaysInMonth(member.birthdayMonth, member.birthdayYear).map(d => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="join-form-group">
                                            <select
                                                value={member.birthdayYear}
                                                onChange={(e) => updateMember(index, 'birthdayYear', e.target.value)}
                                            >
                                                <option value="">Year</option>
                                                {getYears().map(y => (
                                                    <option key={y.value} value={y.value}>{y.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            className="join-add-member-btn"
                            onClick={addMember}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add Family Member
                        </button>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        className="join-submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                Submitting...
                            </>
                        ) : (
                            'Submit for Review'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default JoinPage;
